import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import SiteSelector from '../components/SiteSelector/SiteSelector';
import PlotComponent from '../components/PlotComponent/PlotComponent';
import SGREForecastInferenceUpload from '../components/SGREForecastInferenceUpload';
import { SelectedChannelsProvider, useSelectedChannels } from '../contexts/SelectedChannelsContext';
import { SelectedSiteProvider, useSelectedSite } from '../contexts/SelectedSiteContext';

const SGRE_FORECAST_CHANNEL_LABEL_MAPPINGS = [
  { pattern: /\.pwp_forecast(?:\.|$)/i, displayName: 'Peak Wave Period' },
  { pattern: /\.swh_forecast(?:\.|$)/i, displayName: 'Significant Wave Height' },
  { pattern: /\.rms_forecast(?:\.|$)/i, displayName: 'WTG Response - Peak to Peak' },
  { pattern: /\.tide_windfarm_forecast(?:\.|$)/i, displayName: 'Tide Windfarm' },
];

const UPLOADED_PREDICTION_PREFIX = '__uploaded_onnx_prediction__:';
const UPLOADED_PREDICTION_LABEL_PREFIX = 'Upload: ';
const RMS_ENVELOPE_SCALE = 2.5;
const RMS_LIMIT_CHANNEL_ID = '__rms_limit_25_cm__';
const RMS_LIMIT_LABEL = 'Limit ±25 cm';
const RMS_LIMIT_METRES = 0.25;

function buildSymmetricRmsEnvelope(values) {
  const max_value = values.map(value => Math.abs(Number(value)) * RMS_ENVELOPE_SCALE);
  return {
    min_value: max_value.map(value => -value),
    max_value,
  };
}

async function defaultLoadLatestSGREForecasts({ backendUrl, user, siteId, date, signal }) {
  const options = {
    headers: { Authorization: `Bearer ${user.token}` },
    params: date ? { date } : undefined,
    signal,
  };
  const response = await axios.get(`${backendUrl}/forecasts/latest/site/${siteId}`, options);
  const metadata = response.data;
  const runs = Array.isArray(metadata?.runs) ? metadata.runs.filter(val => /rms/.test(val.channel)) : [];
  const seriesResponses = await Promise.all(runs.map(run => axios.get(`${backendUrl}/data/channels`, {
    ...options,
    params: {
      id: run.channelId,
      start: run.firstSampleDatetime,
      end: run.lastSampleDatetime,
      points: 2000,
    },
  })));
  const data = {};
  const limitTimestamps = new Set();
  for (const seriesResponse of seriesResponses) {
    for (const [label, series] of Object.entries(seriesResponse.data || {})) {
        data[label] = {
            ...series,
            ...buildSymmetricRmsEnvelope(series.max_value),
        };
        for (const timestamp of series.timestamp ?? []) limitTimestamps.add(timestamp);
    }
  }

  const channels = Array.isArray(metadata?.channels) ? [...metadata.channels] : [];
  const rmsChannel = channels.find(channel => /rms/i.test(channel.name ?? ''));
  if (rmsChannel && limitTimestamps.size > 0) {
    channels.push({
      id: RMS_LIMIT_CHANNEL_ID,
      name: RMS_LIMIT_LABEL,
      fullname: RMS_LIMIT_LABEL,
      source: 'Limit',
      value_type_id: rmsChannel.value_type_id,
      value_type_name: rmsChannel.value_type_name,
      value_type_unit: rmsChannel.value_type_unit,
    });
    const timestamp = Array.from(limitTimestamps).sort((left, right) => left - right);
    data[RMS_LIMIT_LABEL] = {
      timestamp,
      min_value: timestamp.map(() => -RMS_LIMIT_METRES),
      max_value: timestamp.map(() => RMS_LIMIT_METRES),
    };
  }

  return { ...metadata, runs, channels, data };
}

function messageFromError(error) {
  return error?.response?.data?.message || error?.message || 'Failed to load forecasts';
}

export default function SGREForecastPage(props) {
  return (
    <SelectedSiteProvider key={`${props.backendUrl}:${props.user.token}`} initialSiteId="">
      <SelectedChannelsProvider>
        <SGREForecastContent {...props} />
      </SelectedChannelsProvider>
    </SelectedSiteProvider>
  );
}

function SGREForecastContent({
  backendUrl,
  user,
  loadSites,
  loadLatestForecasts = defaultLoadLatestSGREForecasts,
}) {
  const { selectedSite, setSelectedSite } = useSelectedSite();
  const { setAvailableChannels } = useSelectedChannels();
  const [forecast, setForecast] = useState(null);
  const [forecastDate, setForecastDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [interval, setInterval] = useState({ start: null, end: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const requestSequence = useRef(0);

  const addUploadedPredictions = useCallback((uploads) => {
    const parsedUploads = uploads.map((upload, index) => {
      const timestamps = Array.isArray(upload?.result?.timestamps)
        ? upload.result.timestamps.map(value => new Date(value).getTime() / 1000)
        : [];
      const values = Array.isArray(upload?.result?.prediction)
        ? upload.result.prediction.map(Number)
        : [];
      if (!timestamps.length
        || timestamps.length !== values.length
        || timestamps.some(value => !Number.isFinite(value))
        || values.some(value => !Number.isFinite(value))) {
        throw new Error(`${upload?.filename ?? `File ${index + 1}`}: the inference service returned invalid prediction data`);
      }
      return { ...upload, timestamps, values };
    });

    const existingChannels = (forecast?.channels ?? [])
      .filter(channel => !String(channel.id).startsWith(UPLOADED_PREDICTION_PREFIX));
    const rmsChannel = existingChannels.find(channel => /rms/i.test(channel.name ?? ''));
    const filenameCounts = new Map();
    const predictionChannels = parsedUploads.map(upload => {
      const count = (filenameCounts.get(upload.filename) ?? 0) + 1;
      filenameCounts.set(upload.filename, count);
      const label = `${UPLOADED_PREDICTION_LABEL_PREFIX}${upload.filename}${count > 1 ? ` (${count})` : ''}`;
      return {
        id: `${UPLOADED_PREDICTION_PREFIX}${upload.id}`,
        name: label,
        fullname: label,
        source: 'Uploaded CSV',
        value_type_id: rmsChannel?.value_type_id ?? UPLOADED_PREDICTION_PREFIX,
        value_type_name: rmsChannel?.value_type_name ?? 'Prediction',
        value_type_unit: rmsChannel?.value_type_unit ?? '',
        predictionLabel: label,
        prediction: upload,
      };
    });
    const channels = [...existingChannels, ...predictionChannels];
    const data = Object.fromEntries(
      Object.entries(forecast?.data ?? {})
        .filter(([label]) => !label.startsWith(UPLOADED_PREDICTION_LABEL_PREFIX)),
    );
    for (const channel of predictionChannels) {
      data[channel.predictionLabel] = {
        timestamp: channel.prediction.timestamps,
        ...buildSymmetricRmsEnvelope(channel.prediction.values),
      };
    }
    const allTimestamps = parsedUploads.flatMap(upload => upload.timestamps);
    const predictionStart = new Date(Math.min(...allTimestamps) * 1000);
    const predictionEnd = new Date(Math.max(...allTimestamps) * 1000);
    const start = interval.start && interval.start < predictionStart ? interval.start : predictionStart;
    const end = interval.end && interval.end > predictionEnd ? interval.end : predictionEnd;

    setAvailableChannels(channels, channels.map(channel => channel.id));
    setInterval({ start, end });
    setForecast(previous => ({
      ...(previous ?? { runs: [] }),
      channels,
      data,
    }));
  }, [forecast, interval, setAvailableChannels]);

  const clearForecast = useCallback(() => {
    setForecast(null);
    setInterval({ start: null, end: null });
    setAvailableChannels([], []);
  }, [setAvailableChannels]);

  useEffect(() => {
    const sequence = ++requestSequence.current;
    const controller = new AbortController();
    clearForecast();
    setError('');

    if (!selectedSite) {
      setLoading(false);
      return () => controller.abort();
    }

    setLoading(true);
    (async () => {
      try {
        const result = await loadLatestForecasts({
          backendUrl,
          user,
          siteId: selectedSite,
          date: forecastDate,
          signal: controller.signal,
        });
        if (controller.signal.aborted || sequence !== requestSequence.current) return;

        const channels = Array.isArray(result?.channels) ? result.channels : [];
        const runs = Array.isArray(result?.runs) ? result.runs : [];
        const start = result?.interval?.start ? new Date(result.interval.start) : null;
        const end = result?.interval?.end ? new Date(result.interval.end) : null;
        if (runs.length && (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))) {
          throw new Error('The server returned an invalid forecast interval');
        }

        setAvailableChannels(channels, channels.map(channel => channel.id));
        setInterval({ start, end });
        setForecast({ ...result, runs, channels, data: result?.data || {} });
      } catch (loadError) {
        if (!controller.signal.aborted && sequence === requestSequence.current) {
          clearForecast();
          setError(messageFromError(loadError));
        }
      } finally {
        if (!controller.signal.aborted && sequence === requestSequence.current) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [backendUrl, user, selectedSite, forecastDate, refresh, loadLatestForecasts, clearForecast, setAvailableChannels]);

  return (
    <main style={{ display: 'grid', gap: 16, padding: 16, minHeight: '100vh', boxSizing: 'border-box', alignContent: 'start' }}>
      {user.token &&
          (<div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <SiteSelector
              backendUrl={backendUrl}
              user={user}
              loadSites={loadSites}
              onChange={setSelectedSite}
            />
            <label style={{ display: 'grid', gap: 4 }}>
              <span>Forecast date (UTC)</span>
              <input
                type="date"
                value={forecastDate}
                onChange={event => setForecastDate(event.target.value)}
                disabled={loading}
              />
            </label>
            <button type="button" disabled={!selectedSite || loading} onClick={() => setRefresh(value => value + 1)}>
              Refresh forecasts
            </button>
          </div>
      )}

      {user.token && user.hasAnyRole(['siemens-engineer', 'jedimaster']) && (
        <SGREForecastInferenceUpload
          backendUrl={backendUrl}
          user={user}
          onPredictions={addUploadedPredictions}
        />
      )}

      {user.token && !selectedSite && <div style={{ opacity: 0.7 }}>Choose a site to see its forecasts for the selected date.</div>}
      {loading && <h3>Loading newest forecasts…</h3>}
      {error && <div role="alert" style={{ color: 'tomato' }}>{error}</div>}

      {!loading && !error && selectedSite && forecast && forecast.runs.length === 0 && Object.keys(forecast.data ?? {}).length === 0 && (
        <div style={{ opacity: 0.7 }}>No forecasts are available for this site on {forecastDate}.</div>
      )}

      {!loading && !error && forecast && Object.keys(forecast.data ?? {}).length > 0 && (
        <>
          <PlotComponent
            key={`${selectedSite}:${forecastDate}:${refresh}`}
            backendUrl={backendUrl}
            user={user}
            interval={interval}
            onIntervalChange={(start, end) => setInterval({ start, end })}
            initialData={forecast.data}
            loadData={async () => forecast.data}
            showUpdateButton={false}
            disablePsdButton={true}
            disableShowLegendsButton={true}
            channelLabelMappings={SGRE_FORECAST_CHANNEL_LABEL_MAPPINGS}
          />
        </>
      )}
    </main>
  );
}

export { buildSymmetricRmsEnvelope, defaultLoadLatestSGREForecasts };
